import {
  isStyledAnnotation,
  pasteStylePatch,
  styleAnnotations,
  type AnnotationStyle
} from '@peruselab/structure-editor/domain/annotationStyle';
import {
  appearanceTargets,
  styleMolecules,
  type MoleculeAppearancePatch
} from '@peruselab/structure-editor/domain/moleculeAppearance';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_ARMED_BOND_TOOL,
  emptySelection,
  type ArmedBondTool,
  type EditorSelection,
  type EditorTool
} from '@peruselab/structure-editor/domain/editor';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import {
  REACTION_ZONES,
  PLUS_JOINED_ZONES,
  placeInBox,
  plusSignPositions,
  type Point,
  type ReactionZone,
  type Rect
} from '@peruselab/structure-editor/domain/reactionGraph';
import type { PaperPlacement } from '../dnd/payloads';
import { useReactionBoxes } from './useReactionBoxes';
import { useReactionDetails } from './useReactionDetails';
import { placeReactionOnPaper, type ReactionOnPaper } from './reactionOnPaper';
import type { NormalizedPoint } from '@peruselab/structure-editor/model';
import { useDocumentHistory } from './useDocumentHistory';
import { useDrawingProjections } from './useDrawingProjections';
import { useSelectionTargets } from './useSelectionTargets';
import { useDrawingCommands } from './useDrawingCommands';
import { useDrawingGestures } from './useDrawingGestures';
import { useSelectionReconciliation } from './useSelectionReconciliation';
import { useMarkushMode } from './useMarkushMode';
import { withAtomLabelOverrides } from '@peruselab/structure-editor/domain/drawingDocumentProjections';
import { fromMarkushDrawingDocument, markushAttachmentLines } from '@peruselab/structure-editor/domain/markushLayers';
import {
  projectPointPreservingAspect,
  unprojectPointPreservingAspect
} from '@peruselab/structure-editor/domain/aspectProjection';
import type { StructureFix } from '@peruselab/api-client/structureChecks';
import { applyStructureFix } from './structureFixes';

/** #700 -- room (drawing units, about a bond) between molecules added side by side in a box. */
const BOX_GAP = 30;
/** #744 -- room kept around a reaction placed beside what is drawn (as Add to paper keeps). */
const PLACE_REACTION_MARGIN = 30;

/**
 * The drawing workspace's central hook (roadmap items 34-35: see
 * docs/repo-topology-phase-5-use-drawing-document-decomposition.md for the characterization
 * behind this split). `WorkspaceShell.tsx` is this hook's only caller, and it hands the whole
 * returned object to 6 leaf components as one prop-drilled blob rather than destructuring
 * narrowly -- so this function's return shape is a stable external contract, even though its
 * internals are now composed from focused sub-hooks:
 *
 * - `useDocumentHistory` -- the document + undo/redo history, and `isOversizedImport`.
 * - `useDrawingProjections` -- pure coordinate/chemistry derivations of the document.
 * - `useSelectionTargets` -- pure derivations of the current selection.
 * - `useDrawingCommands` -- atom/bond mutation commands, plus delete/clear (which also touch
 *   selection state).
 * - `useDrawingGestures` -- freehand/ring/chain/shape/curved-arrow placement from the paper.
 * - `useSelectionReconciliation` -- the effects keeping tool/selection consistent with the
 *   document (selection is not part of undo/redo history).
 *
 * `tool`/`selection` state stays here rather than inside any one sub-hook: `useDocumentHistory`
 * needs `setSelection` (a document replace/clear also clears selection), and
 * `useSelectionReconciliation`'s effects need to read the live `document` from
 * `useDocumentHistory` -- owning the state at this level, and passing it down as parameters,
 * avoids forcing an arbitrary owner on what is a genuinely two-way relationship.
 */
export function useDrawingDocument(paperSize: PaperSize, onCopySelection?: () => void) {
  const [tool, setTool] = useState<EditorTool>('draw');
  // UI action rules Rule 2: Single is the only default bond type, assigned once here at launch;
  // no effect below resets it. Rule 3: the rail arms it, the drawingPaper acts on it.
  const [armedBondTool, setArmedBondTool] = useState<ArmedBondTool>(DEFAULT_ARMED_BOND_TOOL);
  const [selection, setSelection] = useState<EditorSelection>(emptySelection);
  // #577 -- the Center of Rotation toggle's custom pivot (null = use the selection's centroid).
  // Owned here, not in WorkspaceShell, because transformSelection (useDrawingCommands, below)
  // needs it for rotate-left/right math, and because it has to reset whenever *what* is selected
  // changes -- selection is owned by this hook.
  const [rotationPivot, setRotationPivot] = useState<NormalizedPoint | null>(null);
  const selectedAtomIdsKey = selection.atomIds.join(',');
  const selectedBondIdsKey = selection.bondIds.join(',');
  useEffect(() => {
    setRotationPivot(null);
  }, [selectedAtomIdsKey, selectedBondIdsKey]);

  const {
    document,
    history,
    isOversizedImport,
    setHistory,
    setIsOversizedImport,
    commitDocument,
    applyWithoutHistory,
    previewDocument,
    commitPreview,
    cancelPreview,
    replaceDocument,
    resetDocument,
    commitImportedDocument,
    undo,
    redo,
    handlePaperSizeChange
  } = useDocumentHistory(paperSize, setSelection);

  const [annotationStyleDefaults, setAnnotationStyleDefaults] = useState<AnnotationStyle>({ lineStyle: 'solid' });
  // A property set to `undefined` goes back to the standard look (#859).
  type StylePatch = Partial<Record<keyof AnnotationStyle, AnnotationStyle[keyof AnnotationStyle] | undefined>>;
  const updateAnnotationStyle = useCallback(
    (style: StylePatch) => {
      commitDocument((current) => styleAnnotations(current, selection.annotationIds ?? [], style));
    },
    [commitDocument, selection.annotationIds]
  );
  // #861 (#842 S8) -- Copy style / Paste style. The copied style lasts for the session and is kept
  // apart from the object clipboard. Copy takes the first selected shape, arrow or text box, else
  // (#868) the first selected bond or atom's appearance. Paste gives annotations the copied style
  // and atoms / bonds the copied colour, width and by-element choice -- colour and width carry
  // across between shapes and molecules.
  const [copiedStyle, setCopiedStyle] = useState<AnnotationStyle | null>(null);
  const [copiedByElement, setCopiedByElement] = useState<boolean | undefined>(undefined);
  const copyStyle = useCallback(() => {
    const ids = selection.annotationIds ?? [];
    const source = ids
      .map((id) => document.annotations.find((annotation) => annotation.id === id))
      .find((annotation) => annotation !== undefined && isStyledAnnotation(annotation));
    if (source && isStyledAnnotation(source)) {
      setCopiedStyle(source.style ?? {});
      setCopiedByElement(undefined);
      return true;
    }
    const { atomIds, bondIds } = appearanceTargets(document, selection);
    if (atomIds.length === 0 && bondIds.length === 0) return false;
    const bond = bondIds.length ? document.depiction.bondAppearance?.[bondIds[0]!] : undefined;
    const atom = atomIds.length ? document.depiction.atomAppearance?.[atomIds[0]!] : undefined;
    const color = bond?.color ?? atom?.color;
    setCopiedStyle({
      ...(color !== undefined ? { color } : {}),
      ...(bond?.lineWidth !== undefined ? { lineWidth: bond.lineWidth } : {})
    });
    setCopiedByElement(atom?.colorByElement);
    return true;
  }, [document, selection]);
  const pasteStyle = useCallback(() => {
    const ids = selection.annotationIds ?? [];
    const { atomIds, bondIds } = appearanceTargets(document, selection);
    if (!copiedStyle || (ids.length === 0 && atomIds.length === 0 && bondIds.length === 0)) return false;
    commitDocument((current) =>
      styleMolecules(styleAnnotations(current, ids, pasteStylePatch(copiedStyle)), selection, {
        color: copiedStyle.color,
        lineWidth: copiedStyle.lineWidth,
        colorByElement: copiedByElement
      })
    );
    return true;
  }, [commitDocument, copiedByElement, copiedStyle, document, selection]);

  // #868 (#842 S7) -- molecule appearance for the selected atoms and bonds (a whole molecule or a
  // segment): live preview, one undo step per finished adjustment.
  const previewMoleculeStyle = useCallback(
    (patch: MoleculeAppearancePatch) => previewDocument((current) => styleMolecules(current, selection, patch)),
    [previewDocument, selection]
  );
  const updateMoleculeStyle = useCallback(
    (patch: MoleculeAppearancePatch) => commitDocument((current) => styleMolecules(current, selection, patch)),
    [commitDocument, selection]
  );

  // #859 (#773 C6) -- live preview while adjusting; commit records one undo step, cancel restores.
  const previewAnnotationStyle = useCallback(
    (style: StylePatch) => {
      previewDocument((current) => styleAnnotations(current, selection.annotationIds ?? [], style));
    },
    [previewDocument, selection.annotationIds]
  );

  // ADR 0020 D6 (#1101) -- a structure-check fix: previewed while its button is hovered, applied
  // as one undo step (the preview committed), or cancelled.
  const previewStructureFix = useCallback(
    (fix: StructureFix) => previewDocument((current) => applyStructureFix(current, fix)),
    [previewDocument]
  );
  const applyFix = useCallback(
    (fix: StructureFix) => {
      previewDocument((current) => applyStructureFix(current, fix));
      commitPreview();
    },
    [commitPreview, previewDocument]
  );

  const markush = useMarkushMode(history, commitDocument);
  const { resetMarkushLayers, openMarkushLayers, startWithoutLayers } = markush;

  const {
    coordinateSize,
    lines,
    atoms: projectedAtoms,
    bondOrders,
    aromaticBondIds,
    overriddenBondIds,
    aromaticCenters,
    stereoBonds,
    annotationRenderables,
    metrics
  } = useDrawingProjections(document, paperSize, isOversizedImport);
  // #648: R atoms draw their Markush label instead of the element symbol.
  const atoms = useMemo(
    () => withAtomLabelOverrides(projectedAtoms, markush.markushAtomLabels),
    [projectedAtoms, markush.markushAtomLabels]
  );

  // ADR 0014 -- the reaction boxes (owner, 2026-09-26): with a reaction arrow on the drawing, four
  // boxes surround it, each with its own position and size (useReactionBoxes). Projected here into
  // paper pixels -- the vector layer's own space -- for drawing and for the paper's pointer handling.
  const {
    reactionBoxes,
    resetReactionBoxes,
    moveReactionBox: moveBoxInDocumentUnits,
    reverseReactionArrow,
    commitIntoBox,
    commitReaction,
    settleByArrow,
    finishReactionText,
    alignInReaction
  } = useReactionBoxes(history, commitDocument);
  const reactionZonesLayout = useMemo(() => {
    if (!reactionBoxes.boxes) return null;
    const { width, height } = document.depiction.coordinateSet;
    const toPixels = (point: { readonly x: number; readonly y: number }) => {
      const projected = projectPointPreservingAspect(
        { x: point.x / width, y: point.y / height },
        { width, height },
        paperSize,
        !isOversizedImport
      );
      return { x: projected.x * paperSize.width, y: projected.y * paperSize.height };
    };
    const zones = Object.fromEntries(
      REACTION_ZONES.map((zone) => {
        const { rect, empty } = reactionBoxes.boxes![zone];
        const topLeft = toPixels({ x: rect.minX, y: rect.minY });
        const bottomRight = toPixels({ x: rect.maxX, y: rect.maxY });
        const members = reactionBoxes.molecules.filter((molecule) => molecule.zone === zone);
        const molecules = members.map((molecule) => molecule.atomIds);
        // #739 -- "+" between the reactants, and between the products.
        const plusSigns = PLUS_JOINED_ZONES.includes(zone) ? plusSignPositions(members).map(toPixels) : [];
        return [
          zone,
          {
            rect: { minX: topLeft.x, minY: topLeft.y, maxX: bottomRight.x, maxY: bottomRight.y },
            empty,
            molecules,
            plusSigns
          }
        ];
      })
    ) as unknown as Record<
      ReactionZone,
      { rect: Rect; empty: boolean; molecules: readonly (readonly string[])[]; plusSigns: readonly Point[] }
    >;
    // #1137: document units to the paper's pixels, for marks placed beside the reaction's texts.
    return { zones, toPixels };
  }, [document.depiction.coordinateSet, isOversizedImport, paperSize, reactionBoxes]);

  /** Moves a box and its contents by a paper-normalized offset (the drag on the paper). */
  const moveReactionBox = useCallback(
    (zone: ReactionZone, offset: NormalizedPoint) => {
      const { width, height } = document.depiction.coordinateSet;
      const size = { width, height };
      const origin = unprojectPointPreservingAspect({ x: 0.5, y: 0.5 }, size, paperSize, !isOversizedImport);
      const moved = unprojectPointPreservingAspect(
        { x: 0.5 + offset.x, y: 0.5 + offset.y },
        size,
        paperSize,
        !isOversizedImport
      );
      moveBoxInDocumentUnits(zone, (moved.x - origin.x) * width, (moved.y - origin.y) * height);
    },
    [document.depiction.coordinateSet, isOversizedImport, moveBoxInDocumentUnits, paperSize]
  );

  /** #648/#674: loading a drawing (or starting a new one) replaces whatever is on the canvas,
   *  Markush or not, with a fresh history -- and a loaded drawing starts with no Markush layers. */
  // #705: the reaction's conditions and catalyst marks, beside the drawing like its boxes.
  const reactionDetails = useReactionDetails(document);
  const { resetReactionDetails, changeReactionDetailsWith } = reactionDetails;

  /** #744 -- a saved reaction (laid out by paperFromReaction) onto the paper, one undo step: it
   *  takes the place of the paper's own reaction, if any; loose molecules stay. Its conditions and
   *  catalysts come with it, and undo brings back the previous ones. */
  const placeReaction = useCallback(
    (laid: ReactionOnPaper) => {
      const placed = placeReactionOnPaper(
        document,
        {
          arrowId: reactionBoxes.arrow?.id ?? null,
          atomIds: reactionBoxes.molecules
            .filter((molecule) => molecule.zone !== null)
            .flatMap((molecule) => molecule.atomIds)
        },
        laid,
        PLACE_REACTION_MARGIN
      );
      changeReactionDetailsWith(document, placed.document, laid.conditions, placed.catalystAtomIds);
      const { width, height } = placed.document.depiction.coordinateSet;
      commitReaction(placed.document, placed.reactionAtomIds);
      if (width > paperSize.width || height > paperSize.height) setIsOversizedImport(true);
    },
    [changeReactionDetailsWith, commitReaction, document, paperSize, reactionBoxes, setIsOversizedImport]
  );
  const replaceDrawing = useCallback(
    (next: Parameters<typeof replaceDocument>[0], importPaperSize?: PaperSize) => {
      resetMarkushLayers();
      resetReactionBoxes();
      resetReactionDetails();
      replaceDocument(next);
      if (importPaperSize)
        setIsOversizedImport(
          next.depiction.coordinateSet.width > importPaperSize.width ||
            next.depiction.coordinateSet.height > importPaperSize.height
        );
    },
    [replaceDocument, resetMarkushLayers, resetReactionBoxes, resetReactionDetails, setIsOversizedImport]
  );
  const resetDrawing = useCallback(
    (size: PaperSize) => {
      resetMarkushLayers();
      resetReactionBoxes();
      resetReactionDetails();
      resetDocument(size);
    },
    [resetDocument, resetMarkushLayers, resetReactionBoxes, resetReactionDetails]
  );
  /** #674: an import (SMILES, template) replaces the structure as an undoable step; it starts with
   *  no Markush layers, and undo returns to the previous drawing's. */
  const commitImportedDrawing = useCallback(
    (next: DrawingDocument, importPaperSize: PaperSize) => {
      startWithoutLayers(next);
      commitImportedDocument(next, importPaperSize);
    },
    [commitImportedDocument, startWithoutLayers]
  );

  /** #650: attachment-point lines (label -> ring centre) in the same paper space as `atoms`. */
  const attachmentLines = useMemo(
    () => markushAttachmentLines(atoms, markush.markushLayers),
    [atoms, markush.markushLayers]
  );

  /** #650: the R-group tool's click on paper (not an atom) -- inside a ring it adds an
   *  attachment point there. `point` is paper-normalized, like placeAtom's. */
  const { attachToRing } = markush;
  const attachToRingAt = useCallback(
    (point: NormalizedPoint, label: string): boolean =>
      attachToRing(
        unprojectPointPreservingAspect(
          point,
          { width: document.depiction.coordinateSet.width, height: document.depiction.coordinateSet.height },
          paperSize,
          !isOversizedImport
        ),
        label
      ),
    [attachToRing, document, isOversizedImport, paperSize]
  );

  /** #648: opens an exported Markush JSON file, labels and definitions intact.
   *  Throws (with a user-facing message) on anything that isn't a usable Markush document. */
  const openMarkushDrawing = useCallback(
    (value: unknown) => {
      const opened = fromMarkushDrawingDocument(value);
      openMarkushLayers(opened.document, opened.layers);
      replaceDocument(opened.document);
      setTool('draw');
    },
    [openMarkushLayers, replaceDocument]
  );

  const {
    selectedTransformAtomIds,
    singleAtomId,
    singleBondId,
    selectedAtom,
    selectedFormalCharge,
    selectedResonanceCharge,
    selectedAtomStereo
  } = useSelectionTargets(document, selection, stereoBonds);

  const {
    updateImplicitHydrogenDirection,
    moveSelectedAtoms,
    setPage,
    moveSelection: moveSelectionOnly,
    moveSelectedAnnotations: moveSelectedAnnotationsOnly,
    transformSelection,
    rotateSelection3d,
    placeFunctionalGroup,
    groupError,
    groupsContracted,
    setGroupsContracted,
    showHydrogens,
    hideHydrogens,
    hydrogensShown,
    setHydrogensShown,
    cleanUpSelection,
    isCleaningUp,
    cleanupError,
    alignSelection,
    reorderSelection,
    canAttachCallouts,
    canDetachCallouts,
    attachSelectedCallouts,
    detachSelectedCallouts,
    deleteSelection,
    clearDrawing,
    updateSelectedAtom,
    changeAtomElement,
    placeAtom,
    applyArmedBondTool,
    updateSelectedFormalCharge,
    applyAtomFormalCharge,
    updateSelectedResonanceCharge,
    updateSelectedIsotope,
    updateSelectedRadicalElectronCount,
    copySelection,
    cutSelection,
    pasteClipboard,
    pasteClipboardAt,
    addDrawingAlongside,
    setReactionArrowKind,
    updatePastePosition,
    hasClipboard,
    canCopySelection
  } = useDrawingCommands({
    onCopySelection,
    document,
    selection,
    setSelection,
    // #650: in Markush mode, deleting a ring atom also removes that ring's attachment label.
    commitDocument: markush.commitWithMarkushCleanup,
    setIsOversizedImport,
    paperSize,
    isOversizedImport,
    atoms,
    selectedTransformAtomIds,
    rotationPivot,
    armedBondTool,
    singleAtomId,
    stereoBonds,
    alignInReaction
  });

  // #1137 follow-up -- what a drag leaves in the Reagents & catalysts or Solvents box settles by the
  // arrow (its own undo step).
  const moveSelection = useCallback(
    (positions: Parameters<typeof moveSelectionOnly>[0]) => {
      moveSelectionOnly(positions);
      settleByArrow(Object.keys(positions), selection.annotationIds ?? []);
    },
    [moveSelectionOnly, selection.annotationIds, settleByArrow]
  );
  const moveSelectedAnnotations = useCallback(
    (offset: NormalizedPoint) => {
      moveSelectedAnnotationsOnly(offset);
      settleByArrow([], selection.annotationIds ?? []);
    },
    [moveSelectedAnnotationsOnly, selection.annotationIds, settleByArrow]
  );

  /** #700 -- adds a depicted structure (a molecule, a drawing's or an illustration's) to the paper:
   *  where it was dropped, into a reaction box (beside what is there, away from the arrow, unless it
   *  was dropped at a point), or beside the drawing. Into a box, the box grows to hold it. */
  const addStructures = useCallback(
    (drawn: DrawingDocument, placement?: PaperPlacement) => {
      const zone = placement?.zone;
      const { arrow, boxes, molecules } = reactionBoxes;
      if (!zone || !arrow || !boxes) {
        addDrawingAlongside(drawn, placement?.at ? { paper: placement.at } : undefined);
        return;
      }
      const members = molecules.filter((molecule) => molecule.zone === zone).map((molecule) => molecule.bounds);
      addDrawingAlongside(
        drawn,
        placement.at
          ? { paper: placement.at }
          : { place: (extent) => placeInBox(boxes[zone].rect, members, arrow, extent, BOX_GAP) },
        (next, atomIds) => {
          commitIntoBox(zone, next, atomIds);
          settleByArrow(atomIds, []);
        }
      );
    },
    [addDrawingAlongside, commitIntoBox, reactionBoxes, settleByArrow]
  );

  const {
    handleLinesChange,
    handleRingPlace,
    handleChainPlace,
    handleShapePlace,
    handleShapeChange,
    handleArrowChange,
    handleTextCommit
  } = useDrawingGestures({
    annotationStyleDefaults,
    setHistory,
    setSelection,
    setTool,
    paperSize,
    isOversizedImport,
    armedBondTool,
    selectedAtomIds: selection.atomIds
  });

  useSelectionReconciliation(
    document,
    selection,
    setSelection,
    setTool,
    deleteSelection,
    copySelection,
    cutSelection,
    pasteClipboard,
    undo,
    redo,
    copyStyle,
    pasteStyle
  );

  return {
    ...markush,
    openMarkushDrawing,
    markushAttachmentLines: attachmentLines,
    reactionZonesLayout,
    reactionMolecules: reactionBoxes.molecules,
    // #1137: the boxes as drawn, for the texts that are part of the reaction.
    reactionBoxesShown: reactionBoxes.boxes,
    // #1151 -- what Align and Distribute count as objects, the reaction's parts included.
    reactionParts: reactionBoxes.parts,
    finishReactionText,
    ...reactionDetails,
    reverseReactionArrow,
    moveReactionBox,
    hasReactionArrow: reactionBoxes.arrow !== null,
    attachToRingAt,
    annotationStyleDefaults,
    setAnnotationStyleDefaults,
    updateAnnotationStyle,
    previewAnnotationStyle,
    copyStyle,
    pasteStyle,
    previewMoleculeStyle,
    updateMoleculeStyle,
    hasCopiedStyle: copiedStyle !== null,
    commitStylePreview: commitPreview,
    cancelStylePreview: cancelPreview,
    previewStructureFix,
    applyStructureFix: applyFix,
    cancelStructureFix: cancelPreview,
    document,
    history,
    tool,
    setTool,
    armedBondTool,
    setArmedBondTool,
    selection,
    setSelection,
    rotationPivot,
    setRotationPivot,
    coordinateSize,
    isOversizedImport,
    lines,
    atoms,
    bondOrders,
    aromaticBondIds,
    overriddenBondIds,
    aromaticCenters,
    annotationRenderables,
    stereoBonds,
    metrics,
    selectedTransformAtomIds,
    singleAtomId,
    singleBondId,
    selectedAtom,
    selectedFormalCharge,
    selectedResonanceCharge,
    selectedAtomStereo,
    commitDocument,
    applyWithoutHistory,
    replaceDocument: replaceDrawing,
    resetDocument: resetDrawing,
    commitImportedDocument: commitImportedDrawing,
    // #674: Auto Layout re-lays the same structure out -- it keeps the drawing's Markush layers.
    commitRelaidDocument: commitImportedDocument,
    undo,
    redo,
    handleLinesChange,
    handleRingPlace,
    handleChainPlace,
    handleShapePlace,
    handleShapeChange,
    handleArrowChange,
    handleTextCommit,
    handlePaperSizeChange,
    moveSelectedAtoms,
    setPage,
    moveSelection,
    moveSelectedAnnotations,
    transformSelection,
    rotateSelection3d,
    placeFunctionalGroup,
    groupError,
    groupsContracted,
    setGroupsContracted,
    showHydrogens,
    hideHydrogens,
    hydrogensShown,
    setHydrogensShown,
    cleanUpSelection,
    isCleaningUp,
    cleanupError,
    alignSelection,
    reorderSelection,
    canAttachCallouts,
    canDetachCallouts,
    attachSelectedCallouts,
    detachSelectedCallouts,
    deleteSelection,
    clearDrawing,
    updateImplicitHydrogenDirection,
    updateSelectedAtom,
    changeAtomElement,
    placeAtom,
    applyArmedBondTool,
    updateSelectedFormalCharge,
    applyAtomFormalCharge,
    updateSelectedResonanceCharge,
    updateSelectedIsotope,
    updateSelectedRadicalElectronCount,
    copySelection,
    cutSelection,
    pasteClipboard,
    pasteClipboardAt,
    addDrawingAlongside,
    setReactionArrowKind,
    addStructures,
    placeReaction,
    updatePastePosition,
    hasClipboard,
    canCopySelection
  };
}

export type UseDrawingDocumentResult = ReturnType<typeof useDrawingDocument>;
